import {
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Map } from "ol";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";
import { DamMapItem } from "../types/mapItems";
import { GenericPopup } from "./GenericPopup";

type Props = {
  map: Map;
} & (ClickProps | HoverProps);

type ClickProps = {
  clickData: DamMapItem;
  hoverData?: DamMapItem;
};

type HoverProps = {
  hoverData: DamMapItem;
  clickData?: DamMapItem;
};

export const DamPopup = ({
  hoverData,
  clickData,
  map,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("dam-page");
  const dateFormatter = useDateFormatter();

  const data = clickData ?? hoverData;

  if (!data) return null;

  const { name, description, maintainerOrganization, damOperations } = data;

  return (
    <GenericPopup
      map={map}
      clicked={!!clickData}
      avatarText={name[0]}
      title={name}
      subheader={description ?? ""}
      content={
        <>
          {maintainerOrganization ? (
            <Typography variant="body2" align="left" noWrap>
              {`${t("maintainer-organization")}: ${
                maintainerOrganization.name
              }`}
            </Typography>
          ) : null}
          {damOperations ? (
            <>
              <Typography variant="h6" align="center" noWrap>
                {`${t("dam-operation:list")}`}
              </Typography>
              <Divider />
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("dam-operation:name")}</TableCell>
                    <TableCell align="right">
                      {t("dam-operation:description")}
                    </TableCell>
                    <TableCell align="right">
                      {t("dam-operation:startTime")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {damOperations
                    .sort(
                      (a, b) => b.startTime.getTime() - a.startTime.getTime(),
                    )
                    .map((operation) => (
                      <TableRow key={operation.id}>
                        <TableCell component="th" scope="row">
                          {operation.name}
                        </TableCell>
                        <TableCell align="right">
                          {operation.description}
                        </TableCell>
                        <TableCell align="right">
                          {dateFormatter({ date: operation.startTime })}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </>
          ) : null}
        </>
      }
    />
  );
};
